import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineRounded';
import MoreVertIcon from '@mui/icons-material/MoreVertRounded';
import ReplayIcon from '@mui/icons-material/ReplayRounded';
import TuneIcon from '@mui/icons-material/TuneRounded';
import { useHistory, type HistoryItem } from '../hooks/useHistory';
import { useTranslation } from '../i18n';
import { HistoryPanel, HistoryPanelMessage, HistoryPanelRow, HistoryThumb } from './HistoryPanel';
import {
  MediaDialog,
  useResultUrls,
  isImageEndpoint,
  statusColor,
  promptOf,
  canRemix,
  endpointRoute,
  remixStateFor,
  type UrlEntry,
} from './MediaDialog';

// ─── Single strip row ─────────────────────────────────────────────────────────

function StripItem({
  item,
  entry,
  selected,
  rerunning,
  onOpen,
  onRerun,
  onRemix,
  t,
}: {
  item: HistoryItem;
  entry?: UrlEntry;
  selected: boolean;
  rerunning: boolean;
  onOpen: () => void;
  onRerun: () => void;
  onRemix: () => void;
  t: ReturnType<typeof useTranslation>;
}) {
  const firstUrl = entry?.urls[0];
  const isImage = isImageEndpoint(item.endpoint);
  const ready = item.job_status === 'completed' && !!firstUrl;
  const canRetry = !!item.metadata?.input;
  const hasRemixRoute = canRemix(item);
  const prompt = promptOf(item);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <HistoryPanelRow selected={selected} onClick={ready ? onOpen : undefined}>
      {/* Thumbnail */}
      <HistoryThumb>
        {ready ? (
          isImage ? (
            <Box
              component="img"
              src={firstUrl}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <>
              <Box
                component="video"
                src={entry?.thumbnailUrl ? undefined : `${firstUrl}#t=0.1`}
                poster={entry?.thumbnailUrl}
                muted
                preload={entry?.thumbnailUrl ? 'none' : 'metadata'}
                playsInline
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'common.white',
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 22, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.7))' }} />
              </Box>
            </>
          )
        ) : item.job_status === 'failed' ? (
          <ErrorOutlineIcon sx={{ fontSize: 20, color: 'error.main' }} />
        ) : (
          <CircularProgress size={16} sx={{ color: 'grey.500' }} />
        )}
      </HistoryThumb>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
          <Chip
            size="small"
            label={t.history.status[item.job_status]}
            color={statusColor(item.job_status)}
            sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.75 } }}
          />
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, fontSize: 10 }}
          noWrap
        >
          {new Date(item.created_at).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
        {prompt && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: 10,
              lineHeight: 1.3,
            }}
          >
            {prompt}
          </Typography>
        )}
      </Box>

      {/* Actions menu — stop propagation so it doesn't trigger onOpen */}
      {(canRetry || hasRemixRoute) && (
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Tooltip title={rerunning ? t.history.detail.retrying : ''} arrow>
            <span>
              <IconButton
                size="small"
                disabled={rerunning}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                }}
                sx={{ p: 0.25 }}
              >
                {rerunning ? <CircularProgress size={14} /> : <MoreVertIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {canRetry && (
              <MenuItem onClick={() => { setMenuAnchor(null); onRerun(); }}>
                <ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={t.history.detail.rerun}
                  secondary={t.history.detail.rerunTooltip}
                  secondaryTypographyProps={{ sx: { fontSize: 11, maxWidth: 220, whiteSpace: 'normal' } }}
                />
              </MenuItem>
            )}
            {hasRemixRoute && (
              <MenuItem onClick={() => { setMenuAnchor(null); onRemix(); }}>
                <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={t.history.detail.remix}
                  secondary={t.history.detail.remixTooltip}
                  secondaryTypographyProps={{ sx: { fontSize: 11, maxWidth: 220, whiteSpace: 'normal' } }}
                />
              </MenuItem>
            )}
          </Menu>
        </Box>
      )}
    </HistoryPanelRow>
  );
}

// ─── Strip container ──────────────────────────────────────────────────────────

export function RecentGenerationsStrip({ endpoint }: { endpoint: string }) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { items, loading, retry, presignedUrl } = useHistory({
    endpoint,
    pageSize: 20,
    pollMs: 8_000,
  });
  const urlsMap = useResultUrls(items, presignedUrl);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const selectedItem = selectedId != null ? items.find((it) => it.id === selectedId) ?? null : null;
  const selectedUrls = selectedId != null ? (urlsMap[selectedId]?.urls ?? []) : [];

  const handleRerun = async (item: HistoryItem) => {
    setRetryingId(item.id);
    try {
      await retry(item.id);
    } catch {}
    finally {
      setRetryingId(null);
    }
  };

  const handleRemix = (item: HistoryItem) => {
    const route = endpointRoute(item.endpoint);
    if (!route) return;
    navigate(route, { state: remixStateFor(item) });
  };

  return (
    <>
      <HistoryPanel title="Recent">
        {loading && items.length === 0 ? (
          <HistoryPanelMessage loading />
        ) : items.length === 0 ? (
          <HistoryPanelMessage>No generations yet</HistoryPanelMessage>
        ) : (
          <Box sx={{ py: 0.5 }}>
            {items.map((it) => (
              <StripItem
                key={it.id}
                item={it}
                entry={urlsMap[it.id]}
                selected={selectedId === it.id}
                rerunning={retryingId === it.id}
                onOpen={() => setSelectedId(it.id)}
                onRerun={() => void handleRerun(it)}
                onRemix={() => handleRemix(it)}
                t={t}
              />
            ))}
          </Box>
        )}
      </HistoryPanel>

      <MediaDialog
        item={selectedItem}
        urls={selectedUrls}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
